import { computed, observable } from 'mobx';
import { ObjectId } from 'bson';
import { GoFeralApiClient } from '../../../clients/GoFeralApiClient';
import { Session } from '../../sessions/Session';

export interface AbstractModelOptions<T extends any> {
  id: ObjectId;
  api_client: GoFeralApiClient;
  data?: T;
}

export abstract class AbstractModel<T extends any> {
  @observable
  accessor data: T;

  constructor(protected options: AbstractModelOptions<T>) {
    if (options.data) {
      this.setData(options.data);
    }
  }

  abstract refresh(): Promise<any>;

  setData(data: T) {
    this.data = data;
  }

  get id() {
    return this.options.id;
  }

  get api_client() {
    return this.options.api_client;
  }

  get key() {
    return this.options.id.toString();
  }
}

export interface AbstractModelStoreOptions {
  session: Session;
}

export type BsonID = ObjectId | string;

export abstract class AbstractModelStore<T extends AbstractModel<D>, D extends any> {
  @observable
  accessor data: Map<string, T>;

  constructor(protected options: AbstractModelStoreOptions) {
    this.data = new Map();
  }

  @computed protected get client() {
    return this.options.session.api_client;
  }

  protected abstract generateModel(id: ObjectId, data?: D): T;

  stringID(id: BsonID) {
    if (typeof id === 'string') {
      return id;
    }
    return id.toString();
  }

  bsonID(id: BsonID) {
    if (typeof id === 'string') {
      return new ObjectId(id);
    }
    return id;
  }

  ensureModel(id: BsonID, data?: D) {
    const _id = this.stringID(id);
    if (!this.data.has(_id)) {
      let object = this.generateModel(this.bsonID(id), data);
      this.data.set(_id, object);
    }
    return this.data.get(_id);
  }

  getModel(id: BsonID) {
    return this.ensureModel(id);
  }
}
